// src/pages/LandingPage.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FiArchive,
  FiArrowLeft,
  FiArrowRight,
  FiBookOpen,
  FiCheckCircle,
  FiClock,
  FiMail,
  FiMapPin,
  FiPhone,
  FiSearch,
  FiShield,
  FiTrendingUp,
  FiUsers,
  FiZap,
} from "react-icons/fi";
import { getPublicCatalog, publicCoverUrl } from "../api/publicCatalog";
import "../styles/lawAfricaLanding.css";

function money(amount, currency = "KES") {
  if (amount == null || Number.isNaN(Number(amount))) return "";
  try {
    return new Intl.NumberFormat("en-KE", {
      style: "currency",
      currency: String(currency || "KES").toUpperCase(),
      maximumFractionDigits: 0,
    }).format(Number(amount));
  } catch {
    return `${currency || "KES"} ${Number(amount).toLocaleString()}`;
  }
}

function arrivalYear(item) {
  const date = item?.publishedAt || item?.updatedAt || item?.createdAt;
  if (!date) return "";
  const year = new Date(date).getFullYear();
  return Number.isFinite(year) ? String(year) : "";
}

function arrivalLabel(item) {
  return item?.subCategoryName || item?.categoryName || item?.countryName || "LawAfrica publication";
}


function ArrivalCover({ item }) {
  const [failed, setFailed] = useState(false);
  const src = publicCoverUrl(item?.coverImagePath);

  if (src && !failed) {
    return (
      <img
        src={src}
        alt={`Cover of ${item.title}`}
        loading="lazy"
        onError={() => setFailed(true)}
      />
    );
  }

  return (
    <div className="landing-arrivals-cover-fallback">
      <strong>{item?.title || "LawAfrica"}</strong>
      <span>{item?.author || "LawAfrica"}</span>
    </div>
  );
}

function LiveNewTitles() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [index, setIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [paused, setPaused] = useState(false);
  const [error, setError] = useState("");
  const regionRef = useRef(null);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    getPublicCatalog({ newTitlesOnly: true, sort: "newest", page: 1, pageSize: 12 }, controller.signal)
      .then((response) => {
        if (!active) return;
        setItems(response?.items || []);
      })
      .catch((err) => {
        if (!active || err?.name === "AbortError") return;
        setError("New titles are temporarily unavailable.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, []);

  useEffect(() => {
    if (items.length < 2 || paused) return undefined;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return undefined;

    const timer = window.setInterval(() => {
      setIndex((current) => (current + 1) % items.length);
    }, 8000);

    return () => window.clearInterval(timer);
  }, [items.length, paused]);

  const visible = useMemo(() => {
    if (!items.length) return [];
    const count = Math.min(3, items.length);
    return Array.from({ length: count }, (_, offset) => items[(index + offset) % items.length]);
  }, [items, index]);

  function openItem(item) {
    const query = new URLSearchParams({ q: item.title, book: String(item.id) });
    navigate(`/explore?${query.toString()}`);
  }

  function step(direction) {
    if (!items.length) return;
    setIndex((current) => (current + direction + items.length) % items.length);
  }

  return (
    <section
      className="landing-new-arrivals"
      id="new-arrivals"
      aria-labelledby="landing-new-arrivals-title"
      ref={regionRef}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={(event) => {
        if (!regionRef.current?.contains(event.relatedTarget)) setPaused(false);
      }}
    >
      <div className="landing-shell">
        <div className="landing-arrivals-heading">
          <div>
            <span>New arrivals</span>
            <h2 id="landing-new-arrivals-title">Fresh legal titles deserve a proper front door.</h2>
          </div>
          <p>
            Newly published, revised and newly digitised LawAfrica resources are surfaced here so recent work is easy to discover.
          </p>
        </div>

        <div className="landing-arrivals-toolbar">
          <Link to="/explore">Browse available titles <FiArrowRight aria-hidden="true" /></Link>
          {items.length > 1 ? (
            <div className="landing-arrivals-controls" aria-label="New title carousel controls">
              <button type="button" onClick={() => step(-1)} aria-label="Previous publications"><FiArrowLeft /></button>
              <button type="button" onClick={() => step(1)} aria-label="Next publications"><FiArrowRight /></button>
            </div>
          ) : null}
        </div>

        {loading ? (
          <div className="landing-arrivals-state" role="status">Loading new titles…</div>
        ) : error ? (
          <div className="landing-arrivals-state">{error}</div>
        ) : visible.length === 0 ? (
          <div className="landing-arrivals-state">Titles selected by LawAfrica administrators will appear here.</div>
        ) : (
          <div className="landing-arrivals-editorial-grid">
            {visible.map((item, position) => {
              const price = item.publicOffer ? money(item.publicOffer.amount, item.publicOffer.currency) : "View publication";
              const year = arrivalYear(item);
              return (
                <article
                  className={`landing-arrivals-editorial-card ${position === 0 ? "is-featured" : ""}`}
                  key={`${item.id}-${position}`}
                >
                  <button
                    type="button"
                    className="landing-arrivals-card-cover"
                    onClick={() => openItem(item)}
                    aria-label={`View ${item.title}`}
                  >
                    <ArrivalCover item={item} />
                  </button>
                  <div className="landing-arrivals-card-copy">
                    <span>{position === 0 ? "New title" : arrivalLabel(item)}</span>
                    <button type="button" onClick={() => openItem(item)}>{item.title}</button>
                    {item.author ? <p>{item.author}</p> : null}
                    <div className="landing-arrivals-card-meta">
                      {item.edition ? <span>{item.edition}</span> : null}
                      {year ? <span>{year}</span> : null}
                      <strong>{price}</strong>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const year = new Date().getFullYear();

  useEffect(() => {
    document.documentElement.classList.remove("catalog-scroll-page");
    document.body.classList.remove("catalog-scroll-page", "catalog-modal-open");

    return () => {
      document.body.classList.remove("catalog-modal-open");
    };
  }, []);

  function submitSearch(event) {
    event.preventDefault();
    const q = search.trim();
    navigate(q ? `/explore?q=${encodeURIComponent(q)}` : "/explore");
  }

  return (
    <div className="landing">
      <header className="landing-header">
        <div className="landing-announcement" role="region" aria-label="LawAfrica new publications">
          <div className="landing-shell landing-announcement-inner">
            <span className="landing-announcement-badge">New on LawAfrica</span>
            <span className="landing-announcement-copy">New titles and updated legal resources are joining the Digital Hub.</span>
            <a href="#new-arrivals">Discover what’s new <FiArrowRight aria-hidden="true" /></a>
          </div>
        </div>

        <div className="landing-shell landing-header-inner">
          <Link to="/" className="landing-brand" aria-label="LawAfrica home">
            <img src="/logo.png" alt="LawAfrica" />
          </Link>

          <nav className="landing-header-actions" aria-label="Primary navigation">
            <Link to="/explore" className="landing-link-button">Explore</Link>
            <Link to="/login" className="landing-link-button">Sign in</Link>
            <Link to="/register" className="landing-primary-button landing-primary-button-small">Create account</Link>
          </nav>
        </div>
      </header>

      <main>
        <section className="landing-hero">
          <div className="landing-shell landing-hero-grid">
            <div className="landing-hero-copy">
              <span className="landing-eyebrow">LawAfrica Digital Hub</span>
              <h1>Trusted legal publishing, transformed for digital research.</h1>
              <p className="landing-hero-lead">
                For over 15 years, LawAfrica has built a strong foundation in legal and paralegal publishing, serving professionals, institutions, governments and students. The LawAfrica Digital Hub brings that publishing experience into a secure modern platform for discovering, reading and researching Commentaries, Statutes, Journals and Law Reports.
              </p>

              <form className="landing-catalog-search" onSubmit={submitSearch}>
                <FiSearch aria-hidden="true" />
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search legal publications, topics, cases or citations"
                  aria-label="Search LawAfrica catalogue"
                />
                <button type="submit">Search</button>
              </form>

              <div className="landing-hero-actions">
                <Link to="/explore" className="landing-primary-button">Explore the library <FiArrowRight aria-hidden="true" /></Link>
                <Link to="/register" className="landing-secondary-button">Create public account</Link>
              </div>

              <div className="landing-trust-row" aria-label="LawAfrica publishing credentials">
                <span><FiClock aria-hidden="true" /> 15+ years of legal publishing</span>
                <span><FiShield aria-hidden="true" /> Built for professionals &amp; institutions</span>
                <span><FiBookOpen aria-hidden="true" /> Trusted legal content, digitally accessible</span>
              </div>
            </div>

            <aside className="landing-research-card" aria-label="Research workspace">
              <div className="landing-research-card-topline">
                <span>Research workspace</span>
                <FiArchive aria-hidden="true" />
              </div>
              <h2>Everything starts with the source.</h2>
              <p>
                Move from discovery to reading and AI-supported research without leaving the LawAfrica knowledge environment.
              </p>
              <div className="landing-research-list">
                <div>
                  <span className="landing-research-list-icon"><FiSearch /></span>
                  <div><strong>Discover</strong><small>Find relevant legal resources quickly.</small></div>
                </div>
                <div>
                  <span className="landing-research-list-icon"><FiBookOpen /></span>
                  <div><strong>Read</strong><small>Continue your work in the digital reader.</small></div>
                </div>
                <div>
                  <span className="landing-research-list-icon"><FiZap /></span>
                  <div><strong>Research</strong><small>Use Legal AI with responsible legal research guidance.</small></div>
                </div>
              </div>
            </aside>
          </div>
        </section>

        <LiveNewTitles />

        <section
          className="landing-institution-section"
          aria-labelledby="landing-law-reports-title"
        >
          <div className="landing-shell landing-institution-grid">
            <div className="landing-institution-copy">
              <span className="landing-eyebrow">Law Reports</span>
              <h2 id="landing-law-reports-title">
                Authoritative case reporting remains a core part of LawAfrica.
              </h2>
              <p>
                LawAfrica Law Reports are designed to support serious legal research with carefully
                organised case-law resources, editorial context and dependable access to reported
                judicial decisions. We are refining the dedicated Law Reports experience before
                introducing it more widely on the Digital Hub.
              </p>
              <ul>
                <li>
                  <FiCheckCircle aria-hidden="true" />
                  <span>
                    Curated case-law resources prepared for lawyers, judges, academics, students
                    and institutional researchers.
                  </span>
                </li>
                <li>
                  <FiCheckCircle aria-hidden="true" />
                  <span>
                    Structured legal reporting intended to make important decisions easier to
                    discover, read and reference.
                  </span>
                </li>
                <li>
                  <FiCheckCircle aria-hidden="true" />
                  <span>
                    A dedicated Law Reports research experience is being refined for a later
                    release.
                  </span>
                </li>
              </ul>
            </div>

            <aside
              className="landing-engagement-card"
              aria-label="LawAfrica Law Reports overview"
            >
              <div className="landing-engagement-heading">
                <div>
                  <span>LawAfrica legal reporting</span>
                  <h3>Law Reports</h3>
                </div>
                <FiArchive aria-hidden="true" />
              </div>

              <div className="landing-engagement-grid">
                <div>
                  <FiBookOpen aria-hidden="true" />
                  <strong>Reported decisions</strong>
                  <span>Case-law research</span>
                </div>
                <div>
                  <FiSearch aria-hidden="true" />
                  <strong>Structured discovery</strong>
                  <span>Research-oriented access</span>
                </div>
                <div>
                  <FiArchive aria-hidden="true" />
                  <strong>Legal record</strong>
                  <span>Organised reporting</span>
                </div>
                <div>
                  <FiShield aria-hidden="true" />
                  <strong>Trusted platform</strong>
                  <span>LawAfrica environment</span>
                </div>
              </div>

              <div className="landing-engagement-note">
                <FiClock aria-hidden="true" />
                <span>
                  The dedicated Law Reports experience is currently being refined and will be
                  introduced in a later release.
                </span>
              </div>
            </aside>
          </div>
        </section>

        <section className="landing-institution-section">
          <div className="landing-shell landing-institution-grid">
            <div className="landing-institution-copy">
              <span className="landing-eyebrow">For institutions</span>
              <h2>See whether your legal library is being used.</h2>
              <p>
                Institution administration should answer more than “how many users do we have?”. LawAfrica is designed around access, engagement and value-for-money visibility.
              </p>
              <ul>
                <li><FiCheckCircle /> Provision individual users or import them in bulk.</li>
                <li><FiCheckCircle /> Understand active users, logins and genuine reading engagement.</li>
                <li><FiCheckCircle /> See most-read titles and the resources delivering institutional value.</li>
                <li><FiCheckCircle /> Keep access tied to institution subscriptions and eligible users.</li>
              </ul>
              <Link to="/login" className="landing-primary-button">Institution sign in <FiArrowRight aria-hidden="true" /></Link>
            </div>

            <aside className="landing-engagement-card" aria-label="Institution engagement overview">
              <div className="landing-engagement-heading">
                <div><span>Institution insight</span><h3>Engagement overview</h3></div>
                <FiTrendingUp aria-hidden="true" />
              </div>
              <div className="landing-engagement-grid">
                <div><FiUsers /><strong>Eligible users</strong><span>Provisioned access</span></div>
                <div><FiClock /><strong>Reading time</strong><span>Meaningful engagement</span></div>
                <div><FiBookOpen /><strong>Most read</strong><span>Popular titles</span></div>
                <div><FiShield /><strong>Access</strong><span>Logins & active users</span></div>
              </div>
              <div className="landing-engagement-note"><FiCheckCircle /> Metrics are intended to reflect genuine reading activity, not simple page opens.</div>
            </aside>
          </div>
        </section>

        <section className="landing-ai-section">
          <div className="landing-shell landing-ai-grid">
            <div className="landing-ai-symbol" aria-hidden="true"><FiZap /></div>
            <div className="landing-ai-copy">
              <span className="landing-eyebrow">Legal AI</span>
              <h2>AI-assisted legal research, grounded in the LawAfrica experience.</h2>
              <p>
                Legal AI supports legal information retrieval, summarisation and research assistance. It is designed to complement professional legal judgment, with users expected to verify outputs against cited legal sources.
              </p>
              <div className="landing-ai-actions">
                <Link to="/login" className="landing-primary-button">Open Legal AI</Link>
                <span><FiShield aria-hidden="true" /> Legal information and research assistance</span>
              </div>
            </div>
          </div>
        </section>

        <section className="landing-access-section">
          <div className="landing-shell">
            <div className="landing-access-card">
              <div>
                <span className="landing-eyebrow">Start with LawAfrica</span>
                <h2>One sign-in. The legal resources you are entitled to use.</h2>
                <p>
                  Public users can create an account directly. Institution users sign in using accounts provisioned by their Institution Administrator.
                </p>
              </div>
              <div className="landing-access-actions">
                <Link to="/login" className="landing-primary-button">Sign in</Link>
                <Link to="/register" className="landing-secondary-button">Create public account</Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="landing-footer" aria-label="LawAfrica footer">
        <div className="landing-footer-shell">
          <div className="landing-footer-main">
            <section className="landing-footer-brand" aria-label="LawAfrica">
              <div className="landing-footer-logo-card">
                <img src="/logo.png" alt="LawAfrica" />
              </div>
              <p>
                Authoritative legal publications, digital reading and research tools from LawAfrica.
              </p>
              <div className="landing-footer-publications" aria-label="LawAfrica publication types">
                <span><FiBookOpen aria-hidden="true" /> <strong>Commentaries</strong></span>
                <span><FiBookOpen aria-hidden="true" /> <strong>Statutes</strong></span>
                <span><FiBookOpen aria-hidden="true" /> <strong>Journals</strong></span>
              </div>
            </section>

            <nav className="landing-footer-column" aria-label="Platform">
              <h2>Platform</h2>
              <Link to="/explore">Explore</Link>
              <a href="#new-arrivals">New arrivals</a>
              <Link to="/login" className="landing-footer-ai-link"><FiZap aria-hidden="true" /> Legal AI</Link>
            </nav>

            <nav className="landing-footer-column" aria-label="Access">
              <h2>Access</h2>
              <Link to="/login">Sign in</Link>
              <Link to="/register">Public registration</Link>
              <a href="mailto:info@lawafrica.com?subject=LawAfrica%20institution%20access">Institutions</a>
            </nav>

            <section className="landing-footer-contact" aria-label="Contact Information">
              <h2>Contact Information</h2>
              <div><FiArchive aria-hidden="true" /><span>P.O Box 4260-00100</span></div>
              <div><FiMapPin aria-hidden="true" /><span>Longhorn Publishers PLC, Funzi Road, Industrial Area, Nairobi, Kenya</span></div>
              <div><FiMail aria-hidden="true" /><a href="mailto:info@lawafrica.com">info@lawafrica.com</a></div>
              <div><FiPhone aria-hidden="true" /><a href="tel:+254797734012">+254 797 734 012</a></div>
              <div><FiClock aria-hidden="true" /><span>Mon-Fri 08:00 - 17:00 UTC+3</span></div>
            </section>
          </div>

          <div className="landing-footer-bottom">
            <span>© {year} LawAfrica. All rights reserved.</span>
            <span>Digital Hub</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
